import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RequestDtmf } from '../../../../shared/models/generated/ziroModels';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { map, Observable, of, Subscription } from 'rxjs';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroRadioConfig } from '../../../../forms/fields/radio/ziro-radio.component';
import { ZiroCheckboxConfig } from '../../../../forms/fields/checkbox/ziro-checkbox.component';
import { ZiroFileUploadConfig } from '../../../../forms/fields/file-upload/ziro-file-upload.component';
import { ZiroTextareaConfig } from '../../../../forms/fields/textarea/ziro-textarea.component';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import { OfferPromptType, UiCallbackSettings } from '../teams-call-queue-edit-form.component';
import { TeamsCallQueue365GroupResource } from '../../../teams-call-queue-365-group.resource';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';

@Component({
  selector: 'ziro-callback-settings-form',
  imports: [ZiroFormsModule, TranslateModule],
  templateUrl: './callback-settings-form.component.html',
})
export class CallbackSettingsFormComponent
  extends ZiroFormAbstractDirective<UiCallbackSettings>
  implements OnInit, OnDestroy
{
  private _translateService = inject(TranslateService);
  private _route = inject(ActivatedRoute);
  private _msCallQueue365GroupResource = inject(TeamsCallQueue365GroupResource);

  isOfferAudioLoading = signal(true);
  offerAudioUploadURL = signal('');
  offerAudioDownloadName = signal('');
  offerAudioFileExtension = signal('');

  acceptedFileTypes = ['.wav', '.wma', '.mp3'];

  private _isInitialLoad = true;
  private _initialOfferAudioName: string;
  private _tenantId: string;
  private _subs = new Subscription();

  formConfig: ZiroFormConfig;

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.params.tenantId;
    this._initialOfferAudioName = this.entity?.offerAudioUpload?.name;

    const updateSub = this.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<UiCallbackSettings>) => {
      this._updateOfferAudioState(update);
      this._isInitialLoad = false;
    });

    this._subs.add(updateSub);

    this.formConfig = {
      fields: {
        enabled: {
          dataAutomation: 'callback-enabled-shadow',
          label: '',
          hidden: () => true,
          componentConfig: new ZiroCheckboxConfig(),
        },
        requestDtmf: {
          dataAutomation: 'callback-request-dtmf',
          label: 'tkey;teams_call_queue.callbacks.request_dtmf.label',
          componentConfig: new ZiroSelectConfig({
            bindValue: 'value',
            options: [
              { label: '1', value: RequestDtmf.TONE_1 },
              { label: '2', value: RequestDtmf.TONE_2 },
              { label: '3', value: RequestDtmf.TONE_3 },
              { label: '4', value: RequestDtmf.TONE_4 },
              { label: '5', value: RequestDtmf.TONE_5 },
              { label: '6', value: RequestDtmf.TONE_6 },
              { label: '7', value: RequestDtmf.TONE_7 },
              { label: '8', value: RequestDtmf.TONE_8 },
              { label: '9', value: RequestDtmf.TONE_9 },
              { label: '0', value: RequestDtmf.TONE_0 },
              { label: '*', value: RequestDtmf.TONE_STAR },
              { label: '#', value: RequestDtmf.TONE_POUND },
            ],
            placeholder: 'tkey;teams_call_queue.callbacks.request_dtmf.placeholder',
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.callbacks.request_dtmf.label.tooltip',
          required: () => this.formData.enabled,
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
        },
        waitTimeBeforeOfferInSeconds: {
          dataAutomation: 'callback-wait-time',
          label: 'tkey;teams_call_queue.callbacks.wait_time.label',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            placeholder: 'tkey;teams_call_queue.callbacks.wait_time.placeholder',
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.callbacks.wait_time.label.tooltip',
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
          validation: [
            {
              validator: (val: string) => {
                if (!val) {
                  return ZiroFormsValidationState.VALID;
                }
                return Number(val) < 1 || Number(val) > 900
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: this._translateService.instant('tkey;validators.global.min_max.error', { min: 1, max: 900 }),
            },
            {
              validator: (
                val: string,
                numberOfCallsInQueueBeforeOffer: number,
                callToAgentRatioThresholdBeforeOffer: number,
                enabled: boolean,
              ) => {
                if (enabled && !val && !numberOfCallsInQueueBeforeOffer && !callToAgentRatioThresholdBeforeOffer) {
                  return ZiroFormsValidationState.INVALID;
                }
                return ZiroFormsValidationState.VALID;
              },
              injectValuesFromFields: [
                'numberOfCallsInQueueBeforeOffer',
                'callToAgentRatioThresholdBeforeOffer',
                'enabled',
              ],
              message: 'tkey;teams_call_queue.callbacks.threshold.at_least_one.validation',
            },
          ],
        },
        numberOfCallsInQueueBeforeOffer: {
          dataAutomation: 'callback-number-of-calls',
          label: 'tkey;teams_call_queue.callbacks.number_of_calls.label',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            placeholder: 'tkey;teams_call_queue.callbacks.number_of_calls.placeholder',
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.callbacks.number_of_calls.label.tooltip',
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
          validation: [
            {
              validator: (val: string) => {
                if (!val) {
                  return ZiroFormsValidationState.VALID;
                }
                return Number(val) < 0 || Number(val) > 100
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: this._translateService.instant('tkey;validators.global.min_max.error', { min: 0, max: 100 }),
            },
            {
              validator: (
                val: string,
                waitTimeBeforeOfferInSeconds: number,
                callToAgentRatioThresholdBeforeOffer: number,
                enabled: boolean,
              ) => {
                if (enabled && !val && !waitTimeBeforeOfferInSeconds && !callToAgentRatioThresholdBeforeOffer) {
                  return ZiroFormsValidationState.INVALID;
                }
                return ZiroFormsValidationState.VALID;
              },
              injectValuesFromFields: [
                'waitTimeBeforeOfferInSeconds',
                'callToAgentRatioThresholdBeforeOffer',
                'enabled',
              ],
              message: 'tkey;teams_call_queue.callbacks.threshold.at_least_one.validation',
            },
          ],
        },
        callToAgentRatioThresholdBeforeOffer: {
          dataAutomation: 'callback-call-to-agent-ratio',
          label: 'tkey;teams_call_queue.callbacks.call_to_agent_ratio.label',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.NUMBER,
            placeholder: 'tkey;teams_call_queue.callbacks.call_to_agent_ratio.placeholder',
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.callbacks.call_to_agent_ratio.label.tooltip',
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
          validation: [
            {
              validator: (val: string) => {
                if (!val) {
                  return ZiroFormsValidationState.VALID;
                }
                return Number(val) < 1 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID;
              },
              message: this._translateService.instant(
                'tkey;teams_call_queue.callbacks.call_to_agent_ratio.min.validation',
                {
                  min: 1,
                },
              ),
            },
            {
              validator: (
                val: string,
                waitTimeBeforeOfferInSeconds: number,
                numberOfCallsInQueueBeforeOffer: number,
                enabled: boolean,
              ) => {
                if (enabled && !val && !waitTimeBeforeOfferInSeconds && !numberOfCallsInQueueBeforeOffer) {
                  return ZiroFormsValidationState.INVALID;
                }
                return ZiroFormsValidationState.VALID;
              },
              injectValuesFromFields: ['waitTimeBeforeOfferInSeconds', 'numberOfCallsInQueueBeforeOffer', 'enabled'],
              message: 'tkey;teams_call_queue.callbacks.threshold.at_least_one.validation',
            },
          ],
        },
        offerPromptType: {
          dataAutomation: 'callback-offer-prompt-type',
          label: 'tkey;teams_call_queue.callbacks.offer_prompt.header',
          componentConfig: new ZiroRadioConfig<OfferPromptType>({
            stackLabelAndOptions: true,
            buttons: [
              {
                value: OfferPromptType.AUDIO_FILE,
                label: 'tkey;teams_call_queue.callbacks.offer_prompt.upload.label',
              },
              {
                value: OfferPromptType.TTS_MESSAGE,
                label: 'tkey;teams_call_queue.callbacks.offer_prompt.tts.label',
              },
            ],
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.callbacks.offer_prompt.header.tooltip',
          required: () => this.formData.enabled,
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
        },
        offerAudioUpload: {
          dataAutomation: 'callback-offer-audio-upload',
          label: '',
          required: () => this.formData.enabled && this.formData.offerPromptType === OfferPromptType.AUDIO_FILE,
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
          componentConfig: new ZiroFileUploadConfig({
            acceptedFileExtensions: { acceptedExtensions: this.acceptedFileTypes.join(', '), allowOthers: false },
            isAudioFile: true,
          }),
          validation: [
            {
              validator: (val: File) => {
                return !!val && val.name.length > 64
                  ? ZiroFormsValidationState.INVALID
                  : ZiroFormsValidationState.VALID;
              },
              message: this._translateService.instant('tkey;global.validation.file_name.max_length', {
                maxLength: 64,
              }),
            },
            {
              validator: (val: File): ZiroFormsValidationState => {
                if (val?.name !== this._initialOfferAudioName) {
                  return val?.size <= 5000000 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID;
                }
              },
              message: () => ({
                content: 'tkey;shared.fileselect.validators.max_size',
                params: {
                  size: '5 MB',
                },
              }),
            },
          ],
        },
        offerTtsPrompt: {
          dataAutomation: 'callback-offer-tts',
          label: '',
          required: () => this.formData.enabled && this.formData.offerPromptType === OfferPromptType.TTS_MESSAGE,
          hidden: () => this.formData.offerPromptType !== OfferPromptType.TTS_MESSAGE,
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
          componentConfig: new ZiroTextareaConfig({
            placeholder: this._translateService.instant('tkey;teams_call_queue.callbacks.offer_prompt.tts.placeholder'),
          }),
          validation: [
            {
              validator: (val: string, offerPromptType: OfferPromptType) => {
                if (offerPromptType !== OfferPromptType.TTS_MESSAGE) {
                  return ZiroFormsValidationState.VALID;
                }
                return val && val.length > 10 && val.length < 1000
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID;
              },
              injectValuesFromFields: ['offerPromptType'],
              message: () =>
                this._translateService.instant('tkey;validators.global.min_max.char.error', {
                  min: 10,
                  max: 1000,
                }),
            },
          ],
        },
        notificationTargetGroup: {
          dataAutomation: 'callback-notification-target-group',
          label: 'tkey;teams_call_queue.callbacks.notification_group.label',
          componentConfig: new ZiroSelectConfig({
            minSearchLength: 3,
            placeholder: 'tkey;teams_call_queue.callbacks.notification_group.placeholder',
            asyncOptionsFn: (query: string) =>
              this._msCallQueue365GroupResource
                .search(this._tenantId, query)
                .pipe(map((groups) => groups.map((group) => ({ label: group.name, value: group.id })))),
          }),
          labelToolTipIconClass: ZiroIcons.TOOLTIP,
          labelToolTipText: 'tkey;teams_call_queue.callbacks.notification_group.label.tooltip',
          required: () => this.formData.enabled,
          disabled: () => !this.formData.enabled,
          valExcluded: () => !this.formData.enabled,
        },
      },
      options: {
        columnClasses: {
          label: 'text-start',
          input: 'col-12',
        },
      },
    };
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  protected submit(): Observable<void> {
    return of(null);
  }

  triggerSubmitValidation(): void {
    this._setIsFormSubmitted(true);
    this.validateAllFields();
  }

  protected readonly OfferPromptType = OfferPromptType;

  private _updateOfferAudioState(update: ZiroFormsUpdate<UiCallbackSettings>): void {
    const { offerAudioUpload: newOfferAudioUpload } = update.new;
    const { offerAudioUpload: oldOfferAudioUpload } = update.old;

    if (
      (newOfferAudioUpload && newOfferAudioUpload.name !== oldOfferAudioUpload?.name) ||
      (newOfferAudioUpload && this._isInitialLoad)
    ) {
      const lastDot = newOfferAudioUpload.name.lastIndexOf('.');
      this.offerAudioFileExtension.update(() => newOfferAudioUpload.name.substring(lastDot));
      this.isOfferAudioLoading.update(() => true);
      this.offerAudioUploadURL.update(() => URL.createObjectURL(newOfferAudioUpload));
      this.offerAudioDownloadName.update(() => newOfferAudioUpload.name);
      this.isOfferAudioLoading.update(() => false);
    }
  }
}
